<template>
  <div>
    <div class="col-12 p-0 mb-1">
      <div
        class="d-flex justify-content-between flex-nowrap"
        v-if="event.zoom_join_url"
      >
        <div class="text-truncate">
          <span class="">{{ event.zoom_join_url }}</span>
        </div>
        <button
          type="button"
          class="btn p-0 ml-3"
          v-clipboard:copy="event.zoom_join_url"
          v-clipboard:success="zoomJoinUrlCopySuccess"
          v-clipboard:error="zoomJoinUrlCopyError"
        >
          <i class="far fa-link"></i>
        </button>
      </div>
      <div class="mb-2" v-else></div>
    </div>
    <div class="col-12 text-center p-0">
      <div class="d-flex flex-column">
        <div
          v-tooltip="{
            content: isInThePast
              ? $t('EVENTS.TEXT.PAST_EVENTS_CAN_NOT_BE_STARTED')
              : '',
            delay: 100,
            placement: 'top',
            container: 'body',
          }"
        >
          <HMButton
            color="zoom text-nowrap w-100"
            :disabled="!canStartZoomSession || isInThePast"
            @click="startZoomSession"
            :loading="startingZoomSession"
          >
            <div
              class="d-flex justify-content-between align-items-center w-100"
            >
              <span v-if="!isStreaming" class="d-flex flex-grow-1">
                {{ getZoomStartText() }}
              </span>
              <span v-if="isStreaming" class="d-flex flex-grow-1">
                {{ $t('EVENTS.BUTTON.JOIN_ZOOM_SESSION') }}
              </span>
              <span
                class="button-image-container d-flex flex-grow-0 justify-content-center"
              >
                <img
                  class="img-fluid img-restrict zoom"
                  src="/assets/images/zoom.png"
                />
              </span>
            </div>
          </HMButton>
        </div>
        <HMButton
          v-if="isStreaming && !isMeetingEvent"
          color="zoom end text-nowrap w-100 mt-3"
          @click="stopZoomSession"
          :loading="stoppingZoomSession"
        >
          <div class="d-flex justify-content-between align-items-center w-100">
            <span v-if="isStreaming && isSoon" class="d-flex flex-grow-1">
              {{ $t('EVENTS.BUTTON.STOP_ZOOM_SESSION') }}
            </span>
            <span v-if="isStreaming && !isSoon" class="d-flex flex-grow-1">
              {{ $t('EVENTS.BUTTON.UNSCHEDULE_ZOOM_SESSION') }}
            </span>
            <span class="button-image-container d-flex flex-grow-0">
              <img
                class="img-fluid img-restrict zoom"
                src="/assets/images/zoom.png"
              />
            </span>
          </div>
        </HMButton>
        <div class="text-left" v-if="!isZoomConnected">
          <small
            class="text-muted mt-2"
            v-html="sanitize($t('EVENTS.TEXT.ENABLE_ZOOM_MSG'))"
          >
          </small>
        </div>
        <div
          class="text-left"
          v-else-if="!zoomWebinarsEnabled && streamOptions.webinar"
        >
          <small
            class="text-muted mt-2"
            v-html="sanitize($t('EVENTS.TEXT.ZOOM_ADD_ON_WARNING'))"
          >
          </small>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { errorToMessage } from '../../../core/utils/errors';
import * as EventUtils from '../../../core/utils/events';
import { mapGetters } from 'vuex';
import { sanitize } from '@/core/utils/utils';
import { EVENT_TYPE_MEETING } from '../../../core/utils/events';

export default {
  name: 'EventZoomStreamingComponent',
  props: {
    event: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  computed: {
    ...mapGetters({
      isZoomConnected: 'user/isZoomConnected',
      zoomWebinarsEnabled: 'user/zoomWebinarsEnabled',
      zoomInfo: 'user/zoomInfo',
    }),
    isSoon() {
      const now = moment();
      const eventStartTime = moment(this.event.event_start_datetime);
      return now.add(1, 'h').isAfter(eventStartTime);
    },
    isInThePast() {
      const now = moment();
      const eventDate = moment(this.event.event_end_datetime).add(1, 'day');
      return now.isAfter(eventDate);
    },
    canStartZoomSession() {
      if (!this.isZoomConnected) return false;
      if (this.streamOptions.webinar && !this.zoomWebinarsEnabled) return false;
      return true;
    },
    streamOptions() {
      return EventUtils.getEventStreamOptions(this.event.event_type) || {};
    },
    isStreaming() {
      return this.event.zoom_join_url;
    },
    isSessionStarted() {
      return this.event.zoom_meeting_started;
    },
    selectedZoomConnection() {
      return this.zoomInfo.filter(
        connection => connection.id === this.event.zoom_connection
      )[0];
    },
    selectedZoomConnectionText() {
      const connection = this.selectedZoomConnection;
      if (!connection) return '';
      return ` (${connection.name})`;
    },
    isMeetingEvent() {
      // meetings are 1:1 scheduled events and do not appear on the public calendar
      return this.event.event_type === EVENT_TYPE_MEETING;
    },
  },
  data() {
    return {
      startingZoomSession: false,
      stoppingZoomSession: false,
      markingZoomSessionStarted: false,
    };
  },
  methods: {
    sanitize,
    getZoomStartText() {
      const key = EventUtils.getZoomEventTypeLabel(
        this.event.event_type,
        this.isSoon
      );
      if (!key) return '';
      return `${this.$t(key)} ${this.selectedZoomConnectionText}`;
    },
    zoomJoinUrlCopySuccess() {
      this.$notify({
        group: 'app',
        type: 'success',
        title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
        text: this.$t('NOTIFICATIONS.TEXT.ZOOM_JOIN_LINK_COPIED_TO_CLIPBOARD'),
      });
    },
    zoomJoinUrlCopyError() {
      this.$notify({
        group: 'app',
        type: 'error',
        title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
        text: this.$t('NOTIFICATIONS.TEXT.ZOOM_JOIN_LINK_COPY_ERROR'),
        duration: -1, // stay ON
      });
    },
    async scheduleZoomSession() {
      if (this.event.zoom_meeting_id && this.event.zoom_start_url) {
        this.fetchZoomMeetingDetailsAndStart();
      } else {
        if (this.startingZoomSession) return;
        this.startingZoomSession = true;
        try {
          await this.$store.dispatch(
            'event/scheduleZoomMeeting',
            this.event.id
          );
          this.$notify({
            group: 'app',
            type: 'success',
            title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
            text: this.$t(
              'NOTIFICATIONS.TEXT.ZOOM_SESSION_SUCCESSFULLY_SCHEDULED'
            ),
          });
        } catch (error) {
          this.$notify({
            group: 'app',
            type: 'error',
            title: 'Failed to schedule Zoom Session',
            text: errorToMessage(error.response, `Request failed: ${error}`),
            duration: -1, // stay ON
          });
        } finally {
          this.startingZoomSession = false;
        }
      }
    },
    async fetchZoomMeetingDetailsAndStart() {
      const windowReference = window.open();
      try {
        await this.$store.dispatch('event/refetchZoomMeeting', this.event.id);
        windowReference.location = this.event.zoom_start_url;
      } catch (error) {
        windowReference.close();
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to start Zoom Session',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    async createZoomSession() {
      if (this.event.zoom_meeting_id && this.event.zoom_start_url) {
        this.fetchZoomMeetingDetailsAndStart();
      } else {
        if (this.startingZoomSession) return;

        const windowReference = window.open();
        this.startingZoomSession = true;
        try {
          await this.$store.dispatch('event/createZoomMeeting', this.event.id);
          windowReference.location = this.event.zoom_start_url;
        } catch (error) {
          windowReference.close();
          this.$notify({
            group: 'app',
            type: 'error',
            title: 'Failed to start Zoom Session',
            text: errorToMessage(error.response, `Request failed: ${error}`),
            duration: -1, // stay ON
          });
        } finally {
          this.startingZoomSession = false;
        }
      }
    },
    async startZoomSession() {
      if (this.isSoon) {
        this.createZoomSession();
      } else {
        this.scheduleZoomSession();
      }
    },
    async stopZoomSession() {
      try {
        this.stoppingZoomSession = true;
        await this.$store.dispatch('event/endZoomMeeting', this.event.id);
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('NOTIFICATIONS.TEXT.ZOOM_SESSION_SUCCESSFULLY_STOPPED'),
        });
      } finally {
        this.stoppingZoomSession = false;
      }
    },
    async markZoomSessionStarted() {
      try {
        this.markingZoomSessionStarted = true;
        await this.$store.dispatch(
          'event/markZoomMeetingStarted',
          this.event.id
        );
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'NOTIFICATIONS.TEXT.ZOOM_SESSION_SUCCESSFULLY_MARKED_AS_STARTED'
          ),
        });
      } finally {
        this.markingZoomSessionStarted = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.button-image-container {
  background-color: $white;
  border: 1px solid $medium-gray;
  border-left-color: transparent;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
  min-height: 41px;
  margin: -9px -14px;
  padding: 13px 20px;
  min-width: 110px;

  .img-restrict {
    max-width: 90px;
    max-height: 24px;
    &.zoom {
      max-height: 15px;
    }
  }
}
</style>
